const methods = new Set([
  'form', 'forms', 'sameForm', 'strictForm', 'where', 'whereQueryString',
  'is', 'isNot', 'isStrict', 'isOneOf', 'isOneOfStrict', 'contains', 'containsOneOf',
  'startsWith', 'startsWithOneOf', 'endsWith', 'endsWithOneOf',
  'isGreaterThan', 'isGreaterThanOrEqualTo', 'isLessThan', 'isLessThanOrEqualTo',
  'isInRange', 'isTruthy', 'isTrue', 'isFalse', 'sort', 'take',
])

/** Parse the stored fluent query language without evaluating document text as JavaScript. */
export function navigationQuery<T extends { whereQueryString(query: string): any, toQuery(): any }>(base: T, expression: string): T {
  const source = expression?.trim() || ''
  if (!source) return base

  // Legacy menus also accept ordinary Aeppic query strings.
  if (!/^(?:query\b|[A-Za-z]+\s*\()/.test(source)) {
    return base.whereQueryString(source)
  }

  let offset = 0
  const fail = () => {
    throw Object.assign(new Error('Unsupported application menu query expression'), {
      code: 'UNSUPPORTED_NAVIGATION_QUERY', status: 422,
    })
  }
  const whitespace = () => {
    while (/\s/.test(source[offset] || '') && offset < source.length) offset++
  }
  const consume = (token: string) => {
    whitespace()
    if (source[offset] !== token) fail()
    offset++
  }
  const value = (depth = 0): any => {
    whitespace()
    if (depth > 10) return fail()
    const quote = source[offset]
    if (quote === '"' || quote === "'") {
      offset++
      let text = ''
      while (offset < source.length) {
        const character = source[offset++]
        if (character === quote) return text
        if (character !== '\\') {
          text += character
          continue
        }
        const escaped = source[offset++]
        const escapes = { n: '\n', r: '\r', t: '\t', b: '\b', f: '\f', '\\': '\\', "'": "'", '"': '"', '/': '/' }
        if (escaped === 'u') {
          const hex = source.slice(offset, offset + 4)
          if (!/^[0-9a-f]{4}$/i.test(hex)) return fail()
          text += String.fromCharCode(parseInt(hex, 16))
          offset += 4
        } else if (Object.prototype.hasOwnProperty.call(escapes, escaped)) {
          text += escapes[escaped]
        } else {
          return fail()
        }
      }
      return fail()
    }
    if (source[offset] === '[') {
      offset++
      const values = []
      whitespace()
      while (source[offset] !== ']') {
        values.push(value(depth + 1))
        whitespace()
        if (source[offset] !== ']') consume(',')
      }
      offset++
      return values
    }
    const literal = /^(?:true|false|null|-?\d+(?:\.\d+)?(?:e[+-]?\d+)?)/i.exec(source.slice(offset))
    if (!literal) return fail()
    offset += literal[0].length
    return JSON.parse(literal[0])
  }

  if (source.length > 16384) fail()
  let result: any = base
  if (source.startsWith('query')) {
    offset = 5
    whitespace()
    if (offset === source.length) return base
    consume('.')
  }
  while (offset < source.length) {
    whitespace()
    const match = /^[A-Za-z]+/.exec(source.slice(offset))
    if (!match) fail()
    const name = match[0]
    offset += name.length
    whitespace()
    if ((name === 'and' || name === 'or') && source[offset] !== '(') {
      result = result[name]
    } else {
      if (!methods.has(name) || typeof result?.[name] !== 'function') fail()
      consume('(')
      const args = []
      whitespace()
      while (source[offset] !== ')') {
        args.push(value())
        whitespace()
        if (source[offset] !== ')') consume(',')
      }
      offset++
      result = result[name](...args)
    }
    whitespace()
    if (offset === source.length) break
    consume('.')
    whitespace()
    if (offset === source.length) fail()
  }
  if (typeof result?.toQuery !== 'function') fail()
  return result
}
